<script setup lang="ts">
/**
 * One calendar entry as a pill: accent bar in the entry's own colour, time,
 * title — the piece every event view draws. `compact` is one line for month
 * cells and lanes, `regular` stacks title, time and the secondary text and
 * fills the box it is given (a time-grid block, an agenda row).
 *
 * The pill is a real button: Tab reaches it, Enter and Space click it, the
 * context-menu key opens the host's menu. Hover or focus shows a tooltip with
 * the whole entry after a short delay; the `tooltip` slot replaces its body.
 *
 * Status is shape, not only colour: tentative is hatched, cancelled and
 * declined are struck through and faded, an open invitation is hollow with a
 * dashed frame, free time is hollow.
 *
 * `variant="dot"` drops the fill for a coloured dot — the line of a month cell
 * (`compact`) or a row of the agenda's list (`regular`: dot, span, title with
 * the location under it, calendar name). `past` fades an entry that is over.
 *
 * `look="desktop"` is the native calendar's chip: a straight colour stripe
 * clipped by the rounded corners, accent and tint pulled from the entry's
 * colour, a slightly larger type and a 2px selection ring. The default
 * `classic` keeps the accent border and tint every other consumer has; the
 * event views pass `desktop` down with their own desktop look.
 */
import { ArrowClockwiseIcon } from '@blpde/nx-icons';
import { computed, inject, onBeforeUnmount, ref, watch } from 'vue';
import { i18nInjectionKey } from '../internal';
import { NxTooltip } from '../Tooltip';
import { formatTimeRange } from './eventFormat';
import type { NxCalendarEvent, NxEventDragMode, NxCalendarEventAppearance } from './types';

defineOptions({ inheritAttrs: false });

export interface NxEventChipStatusLabels {
  tentative: string;
  cancelled: string;
  declined: string;
  needsAction: string;
  recurring: string;
  free: string;
}

const props = withDefaults(
  defineProps<{
    event: NxCalendarEvent;
    appearance?: NxCalendarEventAppearance;
    size?: 'compact' | 'regular';
    /** `filled`: tinted pill with an accent bar. `dot`: no fill, a dot in the entry's colour. */
    variant?: 'filled' | 'dot';
    /** `classic`: accent border and tint. `desktop`: the native calendar's stripe, tint and ring. */
    look?: 'classic' | 'desktop';
    /** The entry is over: drawn faded. The views set it with their `dimPast`. */
    past?: boolean;
    /** Shows the start time (or the span in `regular`). Defaults to timed entries only. */
    showTime?: boolean;
    /** HTML drag of the whole pill. Never for a `readonly` entry. */
    draggable?: boolean;
    /** A handle that drags the end. Never for `readonly` or all-day entries. */
    resizable?: boolean;
    selected?: boolean;
    /**
     * Draws the keyboard focus ring inside the chip instead of around it — for
     * a host that clips the chip to its box (a team lane's time slot), where
     * the outside ring would be cut off.
     */
    insetFocus?: boolean;
    /** Off: no hover tooltip (the accessible name still carries everything). */
    tooltip?: boolean;
    locale?: string;
    untitledLabel?: string;
    allDayLabel?: string;
    resizeLabel?: string;
    statusLabels?: Partial<NxEventChipStatusLabels>;
  }>(),
  {
    size: 'compact',
    variant: 'filled',
    look: 'classic',
    past: false,
    showTime: undefined,
    draggable: false,
    resizable: false,
    selected: false,
    insetFocus: false,
    tooltip: true,
    locale: undefined,
    untitledLabel: '(Ohne Titel)',
    allDayLabel: 'Ganztägig',
    resizeLabel: 'Dauer ändern',
    statusLabels: () => ({}),
  }
);

const emit = defineEmits<{
  /** After the drag data is set; `mode` says whether the pill or its end handle moves. */
  dragStart: [mode: NxEventDragMode, event: DragEvent];
  dragEnd: [event: DragEvent];
}>();

defineSlots<{
  /** Body of the hover tooltip. */
  tooltip?: (props: { event: NxCalendarEvent }) => unknown;
}>();

const DEFAULT_STATUS: NxEventChipStatusLabels = {
  tentative: 'Vorläufig',
  cancelled: 'Abgesagt',
  declined: 'Abgelehnt',
  needsAction: 'Antwort offen',
  recurring: 'Serientermin',
  free: 'Frei',
};

const i18n = inject(i18nInjectionKey, null);
const resolvedLocale = computed(() => props.locale ?? i18n?.locale?.value ?? undefined);
const labels = computed(() => ({ ...DEFAULT_STATUS, ...props.statusLabels }));

const title = computed(() => props.event.title || props.untitledLabel);
const timed = computed(() => !props.event.allDay);
const showsTime = computed(() => props.showTime ?? timed.value);
const startLabel = computed(() =>
  timed.value ? formatTimeRange(props.event.start, null, resolvedLocale.value) : ''
);
const spanLabel = computed(() =>
  timed.value
    ? formatTimeRange(props.event.start, props.event.end, resolvedLocale.value)
    : props.allDayLabel
);
/** The agenda's list row: dot, span, title over location, calendar name. */
const isRow = computed(() => props.variant === 'dot' && props.size === 'regular');
/** Under the title of a row: the location, or the secondary text when no calendar name is set apart. */
const rowDetail = computed(
  () => props.event.location || (props.event.calendarName ? '' : props.event.secondary) || ''
);
const canDrag = computed(() => props.draggable && !props.event.readonly);
const canResize = computed(() => props.resizable && !props.event.readonly && timed.value);

const statusWords = computed(() => {
  const words: string[] = [];
  const { status, response } = props.event;
  if (status === 'tentative' || response === 'tentative') words.push(labels.value.tentative);
  if (status === 'cancelled') words.push(labels.value.cancelled);
  if (response === 'declined') words.push(labels.value.declined);
  if (response === 'needs-action') words.push(labels.value.needsAction);
  if (props.event.free) words.push(labels.value.free);
  if (props.event.recurring) words.push(labels.value.recurring);
  return words;
});

const heading = computed(() => [props.event.tag, title.value].filter(Boolean).join(' · '));

const accessibleName = computed(() =>
  [
    heading.value,
    spanLabel.value,
    ...statusWords.value,
    props.event.secondary ||
      [props.event.location, props.event.calendarName].filter(Boolean).join(' · '),
  ]
    .filter(Boolean)
    .join(', ')
);

const classes = computed(() => {
  const { status, response, allDay, readonly, free } = props.event;
  return [
    'nx-event-chip',
    `is-${props.size}`,
    `is-${props.variant}`,
    {
      'is-desktop': props.look === 'desktop',
      'is-row': isRow.value,
      'is-past': props.past,
      'is-dense': props.appearance?.density === 'compact',
      'has-title-lines': (props.appearance?.titleLines ?? 1) > 1,
      'is-all-day': allDay,
      'is-tentative': status === 'tentative' || response === 'tentative',
      'is-cancelled': status === 'cancelled',
      'is-declined': response === 'declined',
      'is-needs-action': response === 'needs-action',
      'is-free': free,
      'is-readonly': readonly,
      'is-selected': props.selected,
      'has-inset-focus': props.insetFocus,
    },
  ];
});

const colorStyle = computed(() => ({
  ...(props.event.color ? { '--event-color': props.event.color } : {}),
  '--event-title-lines': Math.min(4, Math.max(1, Math.round(props.appearance?.titleLines ?? 1))),
}));

const root = ref<HTMLElement | null>(null);
const tooltipOpen = ref(false);
let hoverTimer: ReturnType<typeof setTimeout> | undefined;

/** Long enough that sweeping over a busy week does not strobe tooltips. */
const HOVER_DELAY_MS = 300;

function closeTooltip(): void {
  clearTimeout(hoverTimer);
  tooltipOpen.value = false;
  window.removeEventListener('scroll', closeTooltip, true);
}

function openTooltip(): void {
  if (!props.tooltip || props.appearance?.tooltip === false) return;
  clearTimeout(hoverTimer);
  hoverTimer = setTimeout(
    () => {
      tooltipOpen.value = true;
      // A scrolling grid moves the pill away from its bubble.
      window.addEventListener('scroll', closeTooltip, true);
    },
    Math.min(1500, Math.max(100, props.appearance?.tooltipDelay ?? HOVER_DELAY_MS))
  );
}

watch(() => [props.tooltip, props.appearance?.tooltip, props.event.id], closeTooltip);
onBeforeUnmount(closeTooltip);

function onDragStart(event: DragEvent): void {
  closeTooltip();
  const fromHandle = Boolean(
    (event.target as HTMLElement | null)?.closest?.('[data-resize-handle]')
  );
  const mode: NxEventDragMode = fromHandle ? 'resize' : 'move';
  if ((mode === 'move' && !canDrag.value) || (mode === 'resize' && !canResize.value)) return;
  if (event.dataTransfer) {
    event.dataTransfer.effectAllowed = 'move';
    event.dataTransfer.setData(
      'text/plain',
      mode === 'resize' ? `${props.event.id}:resize` : String(props.event.id)
    );
  }
  emit('dragStart', mode, event);
}

function onDragEnd(event: DragEvent): void {
  emit('dragEnd', event);
}

defineExpose({ focus: () => root.value?.focus() });
</script>

<template>
  <button
    ref="root"
    v-bind="$attrs"
    :aria-label="accessibleName"
    :class="classes"
    :data-event-id="event.id"
    :draggable="canDrag ? 'true' : undefined"
    :style="colorStyle"
    type="button"
    @blur="closeTooltip"
    @dragend="onDragEnd"
    @dragstart="onDragStart"
    @focus="openTooltip"
    @mouseenter="openTooltip"
    @mouseleave="closeTooltip"
  >
    <span v-if="variant === 'dot'" class="nx-event-chip__dot" aria-hidden="true"></span>
    <template v-if="isRow">
      <time class="nx-event-chip__time">{{ spanLabel }}</time>
      <span class="nx-event-chip__text">
        <span class="nx-event-chip__main">
          <span v-if="event.tag" class="nx-event-chip__tag">{{ event.tag }}</span>
          <span class="nx-event-chip__title">{{ title }}</span>
        </span>
        <span v-if="rowDetail" class="nx-event-chip__secondary">{{ rowDetail }}</span>
      </span>
      <span v-if="event.calendarName" class="nx-event-chip__calendar">
        {{ event.calendarName }}
      </span>
    </template>
    <template v-else>
      <span v-if="event.tag" class="nx-event-chip__tag">{{ event.tag }}</span>
      <span class="nx-event-chip__main">
        <time v-if="showsTime && size === 'compact'" class="nx-event-chip__time">
          {{ startLabel }}
        </time>
        <span class="nx-event-chip__title">{{ title }}</span>
      </span>
      <time v-if="showsTime && size === 'regular'" class="nx-event-chip__time">
        {{ spanLabel }}
      </time>
      <span v-if="size === 'regular' && event.secondary" class="nx-event-chip__secondary">
        {{ event.secondary }}
      </span>
    </template>
    <ArrowClockwiseIcon
      v-if="event.recurring"
      class="nx-event-chip__recurring nxt-icon-xs"
      aria-hidden="true"
    />
    <span
      v-if="canResize"
      class="nx-event-chip__resize"
      data-resize-handle
      draggable="true"
      :title="resizeLabel"
      aria-hidden="true"
      @click.stop
      @dblclick.stop
      @pointerdown.stop
    ></span>
  </button>
  <NxTooltip v-if="tooltipOpen && root" :anchor="root" :open="true" multiline side="top">
    <template #content>
      <slot name="tooltip" :event="event">
        <span class="nx-event-chip__tip">
          <strong>{{ heading }}</strong>
          <span>{{ spanLabel }}</span>
          <span v-if="event.secondary || event.location">
            {{ event.secondary || event.location }}
          </span>
          <span v-if="statusWords.length">{{ statusWords.join(' · ') }}</span>
        </span>
      </slot>
    </template>
  </NxTooltip>
</template>

<style scoped>
/* The entry's colour drives only the accent and the tint, not a solid fill —
   readable in both themes whatever colour the data brings. The tint is mixed
   into the surface rather than into transparency, so overlapping blocks in a
   time grid do not show the hour rules through each other.

   Two looks share these rules. `classic` (the default): an accent border in
   the entry's colour. `desktop` (`.is-desktop`, written as `:where()` so it
   keeps the specificity of the rule it changes): the accent is pulled a little
   towards the text colour (darker on light, lighter on dark), a straight
   stripe replaces the border, the type is a step larger, and a selected chip
   gets a 2px ring over the stronger tint. */
.nx-event-chip {
  --event-color: var(--nxt-brand-bg);
  --event-accent: var(--event-color);
  --event-tint: color-mix(in srgb, var(--event-color) 14%, var(--nxt-surface-bg));
  --event-tint-strong: color-mix(in srgb, var(--event-color) 24%, var(--nxt-surface-bg));
  --event-time: color-mix(in srgb, var(--event-accent) 70%, var(--nxt-fg));
  position: relative;
  display: flex;
  align-items: center;
  gap: var(--nxt-space-2xs);
  width: 100%;
  min-width: 0;
  min-height: var(--nxt-control-size-xs);
  box-sizing: border-box;
  padding: var(--nxt-space-2xs) var(--nxt-space-xs);
  overflow: hidden;
  border: 0;
  border-inline-start: calc(var(--nxt-hairline) * 3) solid var(--event-accent);
  border-radius: var(--nxt-radius-sm);
  background: var(--event-tint);
  color: var(--nxt-fg);
  font: inherit;
  font-size: var(--nxt-font-size-xs);
  line-height: var(--nxt-line-height-tight);
  text-align: start;
  cursor: pointer;
  transition: background-color var(--nxt-motion-fast) var(--nxt-ease-out);
}

.nx-event-chip:where(.is-desktop) {
  --event-accent: color-mix(in srgb, var(--event-color) 82%, var(--nxt-fg));
  --event-tint: color-mix(in srgb, var(--event-accent) 16%, var(--nxt-surface-bg));
  --event-tint-strong: color-mix(in srgb, var(--event-accent) 26%, var(--nxt-surface-bg));
  --event-time: color-mix(in srgb, var(--event-accent) 72%, var(--nxt-fg));
  padding-inline-start: calc(var(--nxt-space-xs) + var(--nxt-hairline) * 3);
  border-inline-start: 0;
  font-size: calc(var(--nxt-size-unit) * 0.8125);
  transition:
    background-color var(--nxt-motion-fast) var(--nxt-ease-out),
    box-shadow var(--nxt-motion-fast) var(--nxt-ease-out);
}

.nx-event-chip:hover {
  background: var(--event-tint-strong);
}

.nx-event-chip:focus-visible {
  outline: calc(var(--nxt-hairline) * 2) solid var(--nxt-focus-ring);
  outline-offset: var(--nxt-hairline);
}

/* `insetFocus`: the ring inside the chip's edge, for a host that clips it. */
.nx-event-chip.has-inset-focus:focus-visible {
  outline-offset: calc(var(--nxt-hairline) * -2);
}

.nx-event-chip.is-selected {
  box-shadow: inset 0 0 0 var(--nxt-hairline) var(--event-accent);
}

/* Desktop: a 2px ring in the entry's own colour over the stronger tint. */
.nx-event-chip:where(.is-desktop).is-selected {
  background: var(--event-tint-strong);
  box-shadow: inset 0 0 0 calc(var(--nxt-hairline) * 2) var(--event-accent);
}

.nx-event-chip.is-regular {
  flex-direction: column;
  align-items: stretch;
  justify-content: flex-start;
  gap: 0;
  height: 100%;
}

.nx-event-chip:where(.is-desktop).is-regular {
  gap: var(--nxt-hairline);
}

.nx-event-chip.is-all-day:not(.is-desktop) .nx-event-chip__title {
  font-weight: var(--nxt-font-weight-semibold);
}

.nx-event-chip.is-readonly {
  cursor: default;
}

.nx-event-chip__main {
  display: flex;
  align-items: baseline;
  gap: var(--nxt-space-2xs);
  min-width: 0;
  flex: 1;
}

.is-regular .nx-event-chip__main {
  flex: none;
}

.nx-event-chip__tag {
  flex: none;
  font-weight: var(--nxt-font-weight-semibold);
  color: var(--event-time);
}

.is-regular .nx-event-chip__tag {
  align-self: flex-start;
}

.nx-event-chip__time {
  flex: none;
  font-variant-numeric: tabular-nums;
  font-weight: var(--nxt-font-weight-medium);
  color: color-mix(in srgb, var(--event-accent) 60%, var(--nxt-fg-muted));
}

.nx-event-chip:where(.is-desktop) .nx-event-chip__time {
  font-weight: inherit;
}

.is-regular:where(.is-desktop) .nx-event-chip__time {
  color: var(--event-time);
}

.nx-event-chip__title {
  min-width: 0;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.is-regular .nx-event-chip__title {
  font-weight: var(--nxt-font-weight-medium);
}

.nx-event-chip__secondary {
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
  color: var(--nxt-fg-muted);
}

.nx-event-chip__recurring {
  flex: none;
  color: var(--event-accent);
}

.nx-event-chip:where(.is-desktop) .nx-event-chip__recurring {
  color: var(--event-time);
}

.is-regular .nx-event-chip__recurring {
  position: absolute;
  inset-block-start: var(--nxt-space-2xs);
  inset-inline-end: var(--nxt-space-2xs);
}

/* Desktop's accent bar: a straight strip clipped by the chip's rounded corners
   (not a CSS border, which bends along the radius), like the native calendar's
   stripe. */
.nx-event-chip:where(.is-desktop)::before {
  position: absolute;
  inset-block: 0;
  inset-inline-start: 0;
  width: calc(var(--nxt-hairline) * 3);
  background: var(--event-accent);
  content: '';
  pointer-events: none;
}

/* --- dot: no fill, a dot in the entry's colour (month lines, agenda rows) --- */
.nx-event-chip.is-dot {
  gap: var(--nxt-space-xs);
  padding-inline: var(--nxt-space-2xs);
  border-inline-start: 0;
  background: transparent;
}

.nx-event-chip.is-dot::before {
  display: none;
}

.nx-event-chip.is-dot:hover {
  background: color-mix(in srgb, var(--event-accent) 10%, transparent);
}

.nx-event-chip.is-dot.is-selected {
  background: var(--event-tint);
}

.nx-event-chip__dot {
  flex: none;
  width: var(--nxt-dot-size-md);
  height: var(--nxt-dot-size-md);
  box-sizing: border-box;
  border-radius: var(--nxt-radius-full);
  background: var(--event-accent);
}

.is-dot .nx-event-chip__time {
  color: var(--nxt-fg-muted);
}

.is-dot .nx-event-chip__recurring {
  color: var(--nxt-fg-subtle);
}

.is-dot.is-tentative .nx-event-chip__dot,
.is-dot.is-needs-action .nx-event-chip__dot,
.is-dot.is-free .nx-event-chip__dot {
  border: calc(var(--nxt-hairline) * 2) solid var(--event-accent);
  background: transparent;
}

/* The agenda's list row: dot, span, title over location, calendar name. */
.nx-event-chip.is-row {
  flex-direction: row;
  align-items: center;
  gap: var(--nxt-space-sm);
  height: auto;
  padding: var(--nxt-space-xs) var(--nxt-space-sm);
  font-size: var(--nxt-font-size-sm);
  line-height: var(--nxt-line-height-normal);
}

.is-row .nx-event-chip__time {
  flex: none;
  inline-size: calc(var(--nxt-size-unit) * 6.5);
  color: var(--nxt-fg-muted);
}

.nx-event-chip__text {
  display: flex;
  flex-direction: column;
  flex: 1;
  min-width: 0;
}

.is-row .nx-event-chip__title {
  font-weight: var(--nxt-font-weight-medium);
}

.is-row .nx-event-chip__secondary {
  font-size: var(--nxt-font-size-xs);
}

.nx-event-chip__calendar {
  flex: none;
  max-inline-size: 30%;
  overflow: hidden;
  color: var(--nxt-fg-muted);
  text-overflow: ellipsis;
  white-space: nowrap;
}

.is-row .nx-event-chip__recurring {
  position: static;
}

/* --- status shapes --- */

/* Tentative: hatched, so the state survives a colour-blind reader and print. */
.nx-event-chip.is-filled.is-tentative {
  background: repeating-linear-gradient(
    -45deg,
    var(--event-tint) 0,
    var(--event-tint) var(--nxt-space-2xs),
    var(--nxt-surface-bg) var(--nxt-space-2xs),
    var(--nxt-surface-bg) var(--nxt-space-xs)
  );
}

.nx-event-chip.is-cancelled,
.nx-event-chip.is-declined {
  color: var(--nxt-fg-muted);
  opacity: 0.6;
}

.nx-event-chip.is-cancelled .nx-event-chip__title,
.nx-event-chip.is-declined .nx-event-chip__title {
  text-decoration: line-through;
}

.nx-event-chip.is-filled.is-needs-action {
  background: var(--nxt-surface-bg);
  box-shadow: inset 0 0 0 var(--nxt-hairline) var(--event-accent);
}

.nx-event-chip.is-filled.is-free {
  background: transparent;
  border-inline-start-style: dashed;
  color: var(--nxt-fg-muted);
}

.nx-event-chip.is-filled.is-free::before {
  display: none;
}

/* Over: the tint, the accent and the text step back; the shape stays. */
.nx-event-chip.is-past {
  --event-accent: color-mix(in srgb, var(--event-color) 45%, var(--nxt-surface-bg));
  --event-tint: color-mix(in srgb, var(--event-color) 8%, var(--nxt-surface-bg));
  --event-tint-strong: color-mix(in srgb, var(--event-color) 16%, var(--nxt-surface-bg));
  --event-time: color-mix(in srgb, var(--event-color) 45%, var(--nxt-fg-muted));
  color: var(--nxt-fg-muted);
}

.nx-event-chip.is-past .nx-event-chip__time {
  color: var(--event-time);
}

.nx-event-chip.is-dot.is-past .nx-event-chip__time {
  color: var(--nxt-fg-muted);
}

/* The end handle: the pill's right edge on one line, its foot in a block. */
.nx-event-chip__resize {
  position: absolute;
  inset-block: 0;
  inset-inline-end: 0;
  width: var(--nxt-space-xs);
  cursor: ew-resize;
}

.is-regular .nx-event-chip__resize {
  inset-block: auto 0;
  inset-inline: 0;
  width: auto;
  height: var(--nxt-space-xs);
  cursor: ns-resize;
}

.nx-event-chip__tip {
  display: flex;
  flex-direction: column;
  gap: var(--nxt-space-2xs);
}

@media (forced-colors: active) {
  .nx-event-chip {
    border: var(--nxt-hairline) solid canvastext;
    border-inline-start-width: calc(var(--nxt-hairline) * 3);
  }

  .nx-event-chip:where(.is-desktop),
  .nx-event-chip.is-dot {
    border-inline-start: var(--nxt-hairline) solid canvastext;
  }

  .nx-event-chip::before {
    background: canvastext;
  }

  .nx-event-chip__dot {
    forced-color-adjust: none;
    background: canvastext;
  }
}

.nx-event-chip.is-dense {
  font-size: var(--nxt-font-size-xs);
  padding-block: var(--nxt-space-2xs);
}

.nx-event-chip.has-title-lines .nx-event-chip__title {
  display: -webkit-box;
  -webkit-line-clamp: var(--event-title-lines);
  -webkit-box-orient: vertical;
  white-space: normal;
  overflow: hidden;
}

.nx-event-chip.has-title-lines .nx-event-chip__main {
  align-items: flex-start;
}
</style>
